import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api/v1';

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('asm_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('asm_token');
      localStorage.removeItem('asm_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  refresh: (refresh_token) => api.post('/auth/refresh', { refresh_token }),
  me: () => api.get('/auth/me'),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
};

export const dashboardApi = {
  stats: () => api.get('/dashboard/stats'),
};

export const devicesApi = {
  list: (params = {}) => api.get('/devices', { params: { page: 1, page_size: 200, ...params } }),
  get: (id) => api.get(`/devices/${id}`),
  sendCommand: (id, body) => api.post(`/devices/${id}/command`, body),
  events: (id, params = {}) => api.get(`/devices/${id}/events`, { params }),
  update: (id, data) => api.patch(`/devices/${id}`, data),
};

export const employeesApi = {
  list: (params = {}) => api.get('/employees', { params: { page: 1, page_size: 200, ...params } }),
  get: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.patch(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
};

export const teamsApi = {
  list: () => api.get('/teams'),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.patch(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
};

export const policiesApi = {
  list: (params = {}) => api.get('/policies', { params: { page: 1, page_size: 200, ...params } }),
  create: (data) => api.post('/policies', data),
  update: (id, data) => api.put(`/policies/${id}`, data),
  assign: (data) => api.post('/policies/assign', data),
  getEffective: (scopeType, scopeId) => api.get(`/policies/effective/${scopeType}/${scopeId}`),
};

export const alertsApi = {
  list: (params = {}) => api.get('/alerts', { params: { page: 1, page_size: 200, ...params } }),
  ack: (id, reason) => api.post(`/alerts/${id}/ack`, { reason }),
  resolve: (id, reason) => api.post(`/alerts/${id}/resolve`, { reason }),
};

export const mediaApi = {
  screenshots: (employeeId, date) => api.get('/media/screenshots', { params: { employee_id: employeeId, date } }),
  recordings: (employeeId, date) => api.get('/media/recordings', { params: { employee_id: employeeId, date } }),
};

export const analyticsApi = {
  productivity: (params) => api.get('/analytics/productivity', { params }),
  git: (params) => api.get('/analytics/git', { params }),
  ai: (params) => api.get('/analytics/ai', { params }),
  riskScores: () => api.get('/analytics/risk-scores'),
  employeeRisk: (id) => api.get(`/analytics/risk-scores/${id}`),
  reports: (params) => api.get('/reports', { params }),
  report: (id) => api.get(`/reports/${id}`),
  reportHtml: (id) => api.get(`/reports/${id}/html`),
  generateReport: (type) => api.post(`/reports/generate?report_type=${type || 'weekly'}`),
};

export const auditApi = {
  list: (params) => api.get('/audit', { params }),
};

export const remoteApi = {
  startSession: (data) => api.post('/remote/session/start', data),
  stopSession: (data) => api.post('/remote/session/stop', data),
  listSessions: (params = {}) => api.get('/remote/sessions', { params: { page: 1, page_size: 50, ...params } }),
};

export const integrationsApi = {
  connectGithub: (pat) => api.post('/integrations/github/connect', { github_pat: pat }),
  githubStatus: () => api.get('/integrations/github/status'),
};

export const enrollmentApi = {
  list: (params = {}) => api.get('/enrollment-tokens', { params: { page: 1, page_size: 200, ...params } }),
  create: (data) => api.post('/enrollment-tokens', data),
  revoke: (id) => api.post(`/enrollment-tokens/${id}/revoke`),
};

export const usersApi = {
  list: (params = {}) => api.get('/users', { params: { page: 1, page_size: 50, ...params } }),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  resendInvite: (id) => api.post(`/users/${id}/resend-invite`),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`),
};

export default api;
